import { retrieveOccoOwnerUserMyInfo } from '../api/occoOwner.js';
import {
  clearSession,
  ensureActiveSession,
  getIdToken,
  isAuthRedirectInProgress,
  markLoginRequired,
  runLoginFlow,
  signOut,
} from './oauth.js';

export {
  ensureActiveSession,
  getIdToken,
  getStoredTokens,
  hasStoredSession,
  isAuthRedirectInProgress,
  markLoginRequired,
  signOut,
} from './oauth.js';

export function initCognitoSsoEnv(initEnvValue) {
  initEnvValue('POOL_DATA_USER_ID', process.env.POOL_DATA_USER_ID);
  initEnvValue('POOL_DATA_CLIENT_ID', process.env.POOL_DATA_CLIENT_ID);
  initEnvValue('COGNITO_DOMAIN', process.env.COGNITO_DOMAIN);
  initEnvValue('COGNITO_REDIRECT_URI', process.env.COGNITO_REDIRECT_URI);
  initEnvValue('COGNITO_LOGOUT_REDIRECT_URI', process.env.COGNITO_LOGOUT_REDIRECT_URI);
}

export function getApiKey(callback) {
  getIdToken()
    .then((token) => callback(null, token))
    .catch((err) => {
      if (isAuthRedirectInProgress(err)) {
        return;
      }
      clearSession();
      markLoginRequired();
      callback({ type: 'unauthorized_user', innerError: err }, null);
    });
}

export function setUserSession({ role_id: roleId, username }) {
  if (roleId != null) localStorage.setItem('role', String(roleId));
  if (username != null) localStorage.setItem('username', username);
}

export function getUsername() {
  return localStorage.getItem('username');
}

export function clearUserSession() {
  localStorage.removeItem('role');
  localStorage.removeItem('username');
}

// Clears the local user session and signs out through the Hosted UI. Project-independent,
// so it backs both the auth wrapper's logout and the legacy cognitoApi.signOut.
export function logout() {
  clearUserSession();
  signOut();
}

// Legacy-compatible `cognitoApi` object for projects migrated off the AWS SDK.
// Preserves the original callback(err, token) contract used across photo-album and
// occo-admin (getApiKey / signOut / isLogged) while delegating to the SSO flow, so
// existing call sites work unchanged and the wrapper lives in one place.
export function createCognitoApi() {
  return {
    getApiKey,
    signOut: logout,
    isLogged() {
      return new Promise((resolve, reject) => {
        getApiKey((err) => (err ? reject(err) : resolve()));
      });
    },
  };
}

// Ready-to-use singletons so SDK-migrated projects don't each declare their own
// cognitoApi / authenticationControl module — import these from the package instead.
export const cognitoApi = createCognitoApi();
export const authControl = { logout };

export async function syncOccoOwnerUserSession() {
  const user = await retrieveOccoOwnerUserMyInfo();
  setUserSession(user);
  return user;
}

function showLoginError(err, { errorElId = 'err_msg', statusElId = 'loginStatus' } = {}) {
  const statusEl = document.getElementById(statusElId);
  const errMsgEl = document.getElementById(errorElId);
  if (statusEl) statusEl.hidden = true;
  if (errMsgEl) errMsgEl.textContent = err?.message || 'Sign-in failed. Please try again.';
}

export function createOccoSsoAuth({
  redirectAfterLogin,
  loginPath = './index.html',
  onLoginSuccess = syncOccoOwnerUserSession,
} = {}) {
  if (!redirectAfterLogin) {
    throw new Error('redirectAfterLogin is required');
  }

  return {
    initLoginPage(showLoginPage) {
      if (!showLoginPage) return;
      runLoginFlow({
        redirectAfterLogin,
        onSuccess: onLoginSuccess,
        onError: showLoginError,
      });
    },

    logout,

    guardPage({ onAuthenticated, loginPath: customLoginPath } = {}) {
      // Page load only: confirm Cognito has not revoked the session (admin Sign out).
      ensureActiveSession()
        .then(() => onAuthenticated?.())
        .catch((err) => {
          if (isAuthRedirectInProgress(err)) {
            return;
          }
          clearSession();
          markLoginRequired();
          window.location.href = customLoginPath || loginPath;
        });
    },
  };
}
